import { useEffect, useMemo, useState } from "react";
import type { WatchRule } from "@/shared/lib/types";
import { isSlotConsumingWatch } from "@/domains/watches/lib/watchFormatting";

export function useWatchListState(params: {
  watches: WatchRule[];
  loading: boolean;
  entitlementActiveWatches?: number;
  maxActiveWatchSlots?: number;
  itemsPerPage?: number;
}) {
  const [watchSearch, setWatchSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = params.itemsPerPage ?? 10;

  const filteredWatches = useMemo(() => {
    const query = watchSearch.trim().toLowerCase();
    if (!query) return params.watches;

    return params.watches.filter((watch: WatchRule) => {
      const createdAt = watch.created_at
        ? new Date(watch.created_at * 1000).toLocaleDateString()
        : "";
      return [
        watch.ticker,
        watch.status,
        watch.intent,
        watch.option_contract,
        watch.intent_params?.target_price,
        watch.intent_params?.threshold,
        createdAt,
        watch.watch_id,
      ].some((value) =>
        String(value || "").replace(/_/g, " ").toLowerCase().includes(query),
      );
    });
  }, [params.watches, watchSearch]);

  const totalPages = Math.ceil(filteredWatches.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedWatches = filteredWatches.slice(startIndex, endIndex);
  const liveActiveWatchCount = params.watches.filter((watch: WatchRule) =>
    isSlotConsumingWatch(watch.status),
  ).length;
  const activeWatchCount =
    params.loading && params.watches.length === 0
      ? (params.entitlementActiveWatches ?? liveActiveWatchCount)
      : liveActiveWatchCount;

  useEffect(() => {
    // Preserve the previous WatchesTab behavior: a new search starts on page 1.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(0);
  }, [watchSearch]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      // Keep pagination in range when filtering shrinks the result set.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentPage(totalPages - 1);
    }
  }, [currentPage, totalPages]);

  return {
    watchSearch,
    setWatchSearch,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    filteredWatches,
    paginatedWatches,
    activeWatchCount,
    maxActiveWatchSlots: params.maxActiveWatchSlots ?? 10,
  };
}
